import { IS_TOUCH, state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import { INTERP_DELAY, bulletColor, bulletDr } from "./types.js";
import { drawBeams, drawExplosions } from "./effects.js";
import { drawFlagMarkers, drawFlags } from "./flags.js";
import { drawFogOverlay, drawVisionClipped, fogView, isPointVisible, isVisible } from "./fog.js";
import { drawParticles, drawRings, drawTankFx, spawnBurst, spawnDust } from "./particles.js";
import { drawPowerups } from "./powerups.js";
import { drawScope } from "./scope.js";
import { consumeEffects, interpolated } from "./snapshots.js";
import { drawTank } from "./tanks/index.js";
import { drawFloor, drawMapFeatures, drawWalls } from "./world.js";

export function render(rd: Renderer, localId: string, nowMs: number): void {
  const { ctx, maze } = rd;
  if (!maze) return;
  // Re-fit the backing store when the effective scale drifts (window resize,
  // quality change). Tolerance avoids reallocating on sub-pixel layout jitter.
  if (Math.abs(rd.dpr - rd.currentDpr()) > 0.05) {
    rd.resizeDrawingBuffer(rd.worldWidth, rd.worldHeight);
  }

  // Per-frame dt for particle integration + shake decay (capped so a stalled
  // tab doesn't fling particles across the arena on resume).
  const dt = rd.lastFrameAt ? Math.min(0.05, (nowMs - rd.lastFrameAt) / 1000) : 0.016;
  rd.lastFrameAt = nowMs;

  // Fire any effects the interpolation clock has now reached (delayed in lockstep
  // with the world below), then draw the world ~INTERP_DELAY in the past.
  consumeEffects(rd, nowMs - INTERP_DELAY, nowMs);

  const interp = interpolated(rd, nowMs);
  rd.displayedSnap = interp;
  const localTank = interp?.tanks.find((t) => t.id === localId) ?? null;
  const fog = fogView(rd, localTank, interp?.tanks ?? []);

  // Clear canvas before applying camera transforms
  ctx.clearRect(0, 0, maze.width, maze.height);

  // Touch camera zoom is pinch-adjustable (state.touchZoom, set in input.ts);
  // at ~1 the whole arena is visible so the transform is skipped entirely.
  const zoomFactor = state.touchZoom;
  const useZoom = IS_TOUCH && localTank && zoomFactor > 1.01;

  ctx.save();
  // Screen shake: jitter the whole world transform, decaying each frame.
  if (rd.shakeMag > 0.05) {
    ctx.translate(
      (Math.random() * 2 - 1) * rd.shakeMag,
      (Math.random() * 2 - 1) * rd.shakeMag
    );
    rd.shakeMag *= Math.max(0, 1 - dt * 7);
  } else {
    rd.shakeMag = 0;
  }
  if (useZoom && localTank) {
    const worldWidth = rd.worldWidth;
    const worldHeight = rd.worldHeight;
    // Clamp the camera target so that it doesn't show black empty margins
    const halfW = worldWidth / (2 * zoomFactor);
    const halfH = worldHeight / (2 * zoomFactor);
    const camX = Math.max(halfW, Math.min(localTank.x, worldWidth - halfW));
    const camY = Math.max(halfH, Math.min(localTank.y, worldHeight - halfH));

    // Translate to center of canvas, apply zoom, and translate to focused position
    ctx.translate(worldWidth / 2, worldHeight / 2);
    ctx.scale(zoomFactor, zoomFactor);
    ctx.translate(-camX, -camY);
  }

  applyWallHp(rd);
  drawFloor(rd, maze);
  drawVisionClipped(rd, fog, () => drawMapFeatures(rd, nowMs, fog));
  drawWalls(rd, maze, nowMs);
  if (!interp) {
    if (fog) drawFogOverlay(rd, fog, maze);
    ctx.restore();
    return;
  }

  // Kick up dust behind tanks that moved since the last frame.
  spawnDust(rd, interp.tanks, nowMs);

  // Draw all dynamic clipped elements (powerups, flags, bullets, aiming guide, tanks, beams, explosions)
  // inside a single clipping context to drastically reduce GPU/CPU path clipping overhead on mobile.
  drawVisionClipped(rd, fog, () => {
    drawPowerups(rd, rd.latest()?.powerups ?? [], nowMs, fog);
    drawFlags(rd, interp, nowMs, fog);

    const tankColors = new Map(interp.tanks.map((t) => [t.id, t.color]));
    for (const b of interp.bullets) {
      if (fog && !isPointVisible(b.x, b.y, fog)) continue;
      const rad = Math.max(1, rd.bulletR + bulletDr(b));
      if (state.realisticEnabled) {
        ctx.fillStyle = "#3a3a2a";
      } else if (state.modernEnabled) {
        ctx.fillStyle = "#00f8f8";
      } else if (state.battleCityEnabled) {
        ctx.fillStyle = "#f8f8f8";
      } else {
        ctx.fillStyle = bulletColor(tankColors.get(b.ownerId));
      }
      if (b.homing) {
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.dir ?? 0);
        const r = rad + 1.5;
        ctx.beginPath();
        ctx.moveTo(r, 0);
        ctx.lineTo(-r * 0.8, r * 0.8);
        ctx.lineTo(-r * 0.8, -r * 0.8);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      } else {
        ctx.beginPath();
        ctx.arc(b.x, b.y, rad, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    drawScope(rd, interp, localId, fog);

    for (const t of interp.tanks) {
      if (fog && t.id !== localId && !isVisible(t, fog)) {
        continue;
      }
      drawTank(rd, t, t.id === localId, nowMs);
      drawTankFx(rd, t, nowMs);
    }

    drawBeams(rd, nowMs, fog);
    drawExplosions(rd, nowMs, fog);
    drawParticles(rd, nowMs, dt);
    drawRings(rd, nowMs);
  });

  if (fog) {
    drawFogOverlay(rd, fog, maze);
    // Redraw the local tank and walls over the dark wash so they stay crisp.
    drawTank(rd, fog.local, true, nowMs);
    drawWalls(rd, maze, nowMs);
    // Flags revealed by config show faintly over the fog (out-of-sight only).
    drawFlagMarkers(rd, interp, nowMs, fog);
  }
  ctx.restore();
}

function applyWallHp(rd: Renderer): void {
  if (!rd.destructibleWalls) return;
  const latest = rd.latest();
  if (!latest) return;
  // The snapshot carries the authoritative set of below-full walls, so reset
  // everything to full first — this restores walls that have regrown.
  rd.wallHp.fill(rd.wallMaxHp);
  for (const w of latest.wallHp) {
    if (w.index < rd.wallHp.length) rd.wallHp[w.index] = w.hp;
  }
  // Fire a debris burst on the frame a wall crumbles (and forget walls that
  // have regrown so a later re-break pops again).
  if (rd.maze) {
    for (let i = 0; i < rd.wallHp.length; i++) {
      if (rd.wallHp[i] <= 0) {
        if (!rd.brokenWalls.has(i)) {
          rd.brokenWalls.add(i);
          const w = rd.maze.walls[i];
          const cx = (w.x1 + w.x2) / 2;
          const cy = (w.y1 + w.y2) / 2;
          spawnBurst(rd, cx, cy, 10, rd.lastFrameAt, {
            color: "#7d6f57",
            speed: 90,
            size: 2.6,
            life: 600,
            drag: 2.5,
            grow: 0.4,
            square: true,
          });
          spawnBurst(rd, cx, cy, 4, rd.lastFrameAt, {
            color: "#8a8070",
            speed: 25,
            size: 4,
            life: 800,
            alpha: 0.4,
            drag: 1,
            grow: 2.2,
          });
        }
      } else {
        rd.brokenWalls.delete(i);
      }
    }
  }
}
